/**
 * PKCE Utility Module
 * 
 * Provides functions to generate the code verifier and code challenge for PKCE as part of the OAuth 2.0 
 * protocol, enhancing security against interception attacks.
 * 
 * This module uses the CryptoJS library to perform cryptographic operations, avoiding issues with the 
 * Web Crypto API in certain [older] browsers.
 * 
 * Author: Victor Ferreira Silva
 * Email: victorsilva@spectrum.life
 * 
 * Functions:
 * - generateCodeVerifier(length): Generates a cryptographically random code verifier.
 * - sha256(plain): Generates a SHA-256 hash of the input string.
 * - base64urlencode(str): Encodes a string in base64 URL-safe format.
 * - generateCodeChallenge(codeVerifier): Generates a code challenge from the code verifier.
 * 
 * Usage:
 * import { generateCodeVerifier, generateCodeChallenge } from '@/utils/pkce';
 * 
 * const codeVerifier = generateCodeVerifier(128);
 * const codeChallenge = await generateCodeChallenge(codeVerifier);
 * 
 * Suggestions:
 * Store the code verifier in sessionStorage to persist it across page reloads:
 * sessionStorage.setItem('my_code_verifier', codeVerifier);
 * 
 * Retrieve the code verifier from sessionStorage when needed:
 * const codeVerifier = sessionStorage.getItem('my_code_verifier');
 */

import CryptoJS from 'crypto-js';

export function generateCodeVerifier(length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let codeVerifier = '';
  for (let i = 0; i < length; i++) {
    codeVerifier += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return codeVerifier;
}

export function sha256(plain) {
  const hash = CryptoJS.SHA256(plain);
  return hash;
}

export function base64urlencode(str) {
  const words = CryptoJS.enc.Base64.stringify(str);
  return words.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function generateCodeChallenge(codeVerifier) {
  const hashed = await sha256(codeVerifier);
  return base64urlencode(hashed);
}